import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import logSettingsFacebookPageViewed from '@alltrails/analytics/events/logSettingsFacebookPageViewed';
import logSettingsFacebookLoginConfigured from '@alltrails/analytics/events/logSettingsFacebookLoginConfigured';
import logCommunityContactsSynced from '@alltrails/analytics/events/logCommunityContactsSynced';
import FacebookLoginAction from '@alltrails/analytics/enums/FacebookLoginAction';
import CommunityContactsSyncSource from '@alltrails/analytics/enums/CommunityContactsSyncSource';
import CommunityContactsSyncLocation from '@alltrails/analytics/enums/CommunityContactsSyncLocation';
// @ts-ignore
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import FacebookSettingsModal from '@alltrails/shared/components/modals/FacebookSettingsModal';
import Button from '@alltrails/shared/denali/components/Button';
import { uploadFacebookTokenToBelay, getFacebookUploadStatus, stopSyncFacebookFriends } from '@alltrails/shared/utils/requests/belayRequests';
import { FacebookStatus } from '@alltrails/shared/types/blazes';
import CustomProvider from 'components/CustomProvider';
import { facebookHandler } from 'utils/OAuthHandlers';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import FacebookLoginButton from '../forms/buttons/FacebookLoginButton';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { ServerCommunicationUtil } from '../../utils/server_communication_util';
import useInterval from '../../hooks/useInterval';

export type FacebookSettingsProps = {
  userId: number;
  facebookAppId: string;
  userFacebookId: string | null;
  unlinkPath: string;
};

const {
  strings: {
    FACEBOOK: { LOGIN_FACEBOOK, FACEBOOK_SETTINGS, SYNC, REFRESH_FRIENDS }
  }
} = StringConstantsMixin;

const renderFacebookButton = (isLogin: boolean, isSynced: boolean, isSyncing: boolean, renderProps: { onClick: () => void }) => {
  if (isLogin) {
    return <FacebookLoginButton text={LOGIN_FACEBOOK} handler={renderProps.onClick} />;
  }
  if (isSynced) {
    return <Button text={REFRESH_FRIENDS} onClick={renderProps.onClick} disabled={isSyncing} testId={REFRESH_FRIENDS} />;
  }
  return <Button text={SYNC} onClick={renderProps.onClick} disabled={isSyncing} testId={SYNC} variant="primary" />;
};

const FacebookSettings = ({ userId, facebookAppId, userFacebookId, unlinkPath }: FacebookSettingsProps) => {
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [delay, setDelay] = useState<number | null>(2000);
  const [facebookStatus, setFacebookStatus] = useState(FacebookStatus.NotSubmitted);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSynced, setIsSynced] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const languageRegionCode = useLanguageRegionCode();

  const closeModal = () => {
    setShowSettingsModal(false);
  };

  const handleUnlinkClick = () => {
    logSettingsFacebookLoginConfigured({ facebook_login_action: FacebookLoginAction.DisconnectLoginWithFacebook });
    ServerCommunicationUtil.postApiEndpoint(
      unlinkPath,
      {},
      () => setIsLoggedIn(false),
      () => console.error('error when unlinking facebook'),
      () => {}
    );
  };

  const facebookLoginHandler = (response: { accessToken: string; userID: string; expiresIn: number }) => {
    if (!response.accessToken) {
      console.error('handleFacebookResponeLogin', response);
      setIsLoggedIn(false);
      return;
    }

    const resp = {
      accessToken: response.accessToken,
      userID: response.userID,
      expiresIn: response.expiresIn
    };

    facebookHandler(
      resp,
      languageRegionCode,
      () => {},
      () => {},
      () => {}
    );
    setIsLoggedIn(true);
    logSettingsFacebookLoginConfigured({ facebook_login_action: FacebookLoginAction.EnableLoginWithFacebook });
  };

  const FacebookButton = (): JSX.Element => (
    <FacebookLogin
      disableMobileRedirect
      appId={facebookAppId}
      autoLoad
      callback={facebookLoginHandler}
      scope="email,user_friends,user_gender,user_birthday,user_location"
      render={(renderProps: { onClick: () => void }) => renderFacebookButton(true, isSynced, isSyncing, renderProps)}
    />
  );

  useEffect(() => {
    if (userId) {
      const fetchFacebookStatus = async () => {
        const { data } = await getFacebookUploadStatus(userId);
        setFacebookStatus(data.contactUploads.facebook.status);
      };

      fetchFacebookStatus().catch(console.error);
    }
  }, []);

  useEffect(() => {
    if (facebookStatus === FacebookStatus.Done) {
      setIsLoggedIn(true);
      setIsSynced(true);
    } else {
      setIsLoggedIn(false);
      setIsSynced(false);
    }
  }, [facebookStatus]);

  const getFacebookStatusPoll = async () => {
    if (accessToken && userId) {
      const { data } = await getFacebookUploadStatus(userId);
      setFacebookStatus(data.contactUploads.facebook.status);

      if (facebookStatus === FacebookStatus.Failed) {
        setIsSyncing(false);
        setDelay(null);
      }
      if (facebookStatus === FacebookStatus.Done) {
        setIsSyncing(false);
        setIsSynced(true);
        setDelay(null);
      }
    }
  };

  useInterval(() => {
    getFacebookStatusPoll();
  }, delay);

  const stopSyncFriends = async () => {
    if (userId) {
      try {
        await stopSyncFacebookFriends(userId);
        setIsSynced(false);
        logCommunityContactsSynced({
          contacts_source: CommunityContactsSyncSource.Facebook,
          sync_location: CommunityContactsSyncLocation.FacebookSettings,
          is_disconnected: true,
          is_refreshed: false,
          num_contacts: -1, // -1 for "null" value
          num_registered_contacts: -1
        });
      } catch (err) {
        console.error('error when stop sync friends', err);
        setIsSynced(true);
      }
    }
  };

  const syncFacebookFriends = async (response: { accessToken: string }) => {
    if (!response.accessToken) {
      console.error('handleFacebookResponseSyncFriends', response);
      return;
    }

    setIsSyncing(true);
    setAccessToken(response.accessToken);
    try {
      await uploadFacebookTokenToBelay(response.accessToken, userId);
      logCommunityContactsSynced({
        contacts_source: CommunityContactsSyncSource.Facebook,
        sync_location: CommunityContactsSyncLocation.FacebookSettings,
        is_disconnected: false,
        is_refreshed: isSynced,
        num_contacts: -1, // -1 for "null" value
        num_registered_contacts: -1
      });
      setIsSyncing(false);
      setIsSynced(true);
    } catch (err) {
      console.error('error when syncing friends', err);
      setIsSynced(false);
    }
  };

  const FacebookSyncFriendsButton = (): JSX.Element => (
    <FacebookLogin
      disableMobileRedirect
      appId={facebookAppId}
      autoLoad
      callback={syncFacebookFriends}
      scope="user_friends"
      render={(renderProps: { onClick: () => void }) => renderFacebookButton(false, isSynced, isSyncing, renderProps)}
    />
  );

  const openSettingsModal = () => {
    logSettingsFacebookPageViewed();
    setShowSettingsModal(true);
  };

  return (
    <CustomProvider>
      {showSettingsModal &&
        createPortal(
          <FacebookSettingsModal
            closeModal={closeModal}
            facebookLoginButton={<FacebookButton />}
            isLoggedIn={isLoggedIn || !!userFacebookId}
            handleDisconnect={handleUnlinkClick}
            facebookSyncFriendsButton={<FacebookSyncFriendsButton />}
            isSynced={isSynced}
            handleUnsync={stopSyncFriends}
          />,
          document.getElementById('modalPortal')
        )}
      <div>
        <FacebookLoginButton text={FACEBOOK_SETTINGS} handler={openSettingsModal} />
      </div>
    </CustomProvider>
  );
};

export default FacebookSettings;
